<div
    class="message-container"
    [class.user-container]="message.author === 'self'"
    [class.response-container]="message.author === 'ai'"
>
    @if (message.author === 'ai') {
        <hive-icon-component [icon]="faSparkles" size="x-large" class="chat-avatar" />
    }

    <div class="chat-bubble-container">
        <!-- Chat bubble -->
        <div
            class="copilot-chat-bubble"
            [class.generating-placeholder]="isPlaceholderMessage(message)"
            [class.user-bubble]="message.author === 'self'"
            [class.response-bubble]="message.author === 'ai'"
        >
            @if (isPlaceholderMessage(message)) {
                @if (message.isError) {
                    <hive-inline-notification
                        class="ai-message-failure"
                        heading="Something went wrong"
                        severity="major"
                    >
                        Your problem has been noted and the team will look into it. Please try to regenerate. If the
                        issue still persists, please try a different prompt.
                    </hive-inline-notification>
                } @else if (longQueryInProgress) {
                    Still working on it...
                } @else {
                    Generating...
                }
            } @else if (message.author === 'ai') {
                <copilot-reasoning-steps
                    [reasoningSteps]="message.variations[message.selectedVariationIndex].reasonings || []"
                    [isLoading]="
                        message.variations[message.selectedVariationIndex].message?.length === 0 && isGenerating
                    "
                />
                <!-- The answer as prose runs and rich blocks (FR-007); an answer with no blocks
                    is one text run. No feature-flag check belongs here, but not because the live
                    answer carries no markers -- a STORED one does, and re-parses on every reload.
                    The flag is applied where blocks reach the conversation, in
                    `CopilotChatController.richBlocksEnabled`, so that one gate covers the live
                    path and the history path together. By the time a variation gets here, it
                    holds only blocks the flag allowed. -->
                @for (segment of answerSegments(message); track $index) {
                    @switch (segment.kind) {
                        @case ('text') {
                            <!-- Only want to use markdown for the AI responses, not user messages.
                                Don't enable Mermaid while the response is streaming, or before it has lazily loaded. -->
                            <markdown
                                [mermaid]="mermaidLoaded() && (!isGenerating || !isLatestResponse)"
                                [mermaidOptions]="mermaidOptions"
                                ngPreserveWhitespaces
                                [disableSanitizer]="false"
                                [data]="$any(segment).text"
                            />
                        }
                        @case ('block') {
                            <copilot-rich-block
                                [block]="$any(segment).block"
                                (activateAction)="onRichBlockAction($event)"
                            />
                        }
                        @case ('pending') {
                            <!-- An anchor whose payload has not arrived (FR-040). The condition is
                                the stream, not a timer. `isGenerating` is the conversation's, so
                                `isLatestResponse` is what stops restored anchors in the history
                                lighting up when the user asks something new — the same pair gates
                                the collapsible sections' loading state below. -->
                            @if (isGenerating && isLatestResponse) {
                                <copilot-rich-block-skeleton [type]="$any(segment).type" />
                            }
                        }
                    }
                }
                <!-- Collapsible answer sections (e.g. fleet-status categories).
                    Expanded while streaming so content is visible as it arrives, then collapse. -->
                <copilot-collapsible-sections
                    [sections]="message.variations[message.selectedVariationIndex].sections || []"
                    [isLoading]="isGenerating && isLatestResponse"
                />
            } @else {
                <!-- User message -->
                <div>
                    {{ message.variations[message.selectedVariationIndex].message }}
                </div>
            }
        </div>

        <!-- Response actions -->
        @if (message.author === 'ai' && !isGenerating) {
            <div
                class="response-actions-bar"
                [class.has-rating]="message.variations[message.selectedVariationIndex].feedbackReceived != null"
            >
                <!-- Response selector -->
                @if (showResponseSelector(message)) {
                    <copilot-response-selector
                        [message]="message"
                        (setVariation)="
                            setVariation.emit({
                                message: message,
                                variation: $event,
                            })
                        "
                    />
                }

                <!-- Feedback buttons -->
                @if (!message.isError) {
                    <hive-button
                        [class.active]="
                            message.variations[message.selectedVariationIndex].feedbackReceived === THUMBS_UP
                        "
                        [disabled]="isFeedbackLoading"
                        emphasis="primary"
                        [icon]="faThumbsUp"
                        hiveTooltip
                        hiveTooltipDescription="Thumbs Up"
                        (click)="
                            submitFeedback.emit({
                                feedback: THUMBS_UP,
                                message: message,
                            })
                        "
                    />

                    <hive-button
                        [class.active]="
                            message.variations[message.selectedVariationIndex].feedbackReceived === THUMBS_DOWN
                        "
                        [disabled]="isFeedbackLoading"
                        emphasis="primary"
                        [icon]="faThumbsDown"
                        hiveTooltip
                        hiveTooltipDescription="Thumbs Down"
                        (click)="
                            submitFeedback.emit({
                                feedback: THUMBS_DOWN,
                                message: message,
                            })
                        "
                    />
                }

                <div class="feedback-disclaimer">
                    @if (
                        message.variations[message.selectedVariationIndex].feedbackReceived === null ||
                        message.variations[message.selectedVariationIndex].feedbackReceived === undefined
                    ) {
                        <span>Generative AI is Experimental. Have feedback? Let us know.</span>
                    }
                </div>

                <!-- Copy -->
                @if (!message.isError) {
                    <hive-button
                        emphasis="tertiary"
                        [icon]="faCopy"
                        hiveTooltip
                        hiveTooltipDescription="Copy message"
                        (click)="clickCopyToClipboard(message)"
                    />
                }

                <!-- Regenerate -->
                @if (isLatestResponse) {
                    <hive-button
                        [disabled]="message.variations.length >= regenerationCountLimit"
                        emphasis="tertiary"
                        [icon]="faRetweet"
                        hiveTooltip
                        [hiveTooltipDescription]="
                            message.variations.length < regenerationCountLimit
                                ? 'Regenerate'
                                : 'Message regeneration limit reached.'
                        "
                        (click)="regenerate.emit(message)"
                    />
                }
            </div>
        }

        <!-- Feedback notifications -->
        @if (isFeedbackSuccessful) {
            <hive-inline-notification
                class="feedback-notification"
                heading="Thanks for your feedback!"
                severity="success"
            >
                It has been submitted successfully.
            </hive-inline-notification>
        }

        @if (isFeedbackFailed) {
            <hive-inline-notification class="feedback-notification" heading="Feedback not saved" severity="major">
                We encountered a problem while saving your feedback. Please try again later.
            </hive-inline-notification>
        }

        @if (showFeedback) {
            <copilot-message-feedback
                class="message-feedback"
                [message]="message"
                [sessionId]="conversationSessionId"
                [feedback]="message.variations[message.selectedVariationIndex].feedbackReceived!"
                (feedbackClose)="closeAdditionalFeedback.emit($event)"
                (feedbackSubmit)="submitAdditionalFeedback.emit($event)"
            />
        }
    </div>
</div>
